import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupReferenceAccordionListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#removeReferenceAccordionListeners();
    this.#cancelAllReferenceAccordionAnimations();
    this.#stopViewportTracking();
    this.#cancelRootTransitionWait();
  }

  #viewportSyncFrame = 0;
  #rootTransitionToken = 0;
  #rootTransitionTimer = 0;
  #rootTransitionCleanup = null;
  #referenceAccordionAnimations = new WeakMap();

  #scheduleViewportSync = () => {
    if (!this.isOpen || this.#viewportSyncFrame) return;

    this.#viewportSyncFrame = requestAnimationFrame(() => {
      this.#viewportSyncFrame = 0;
      this.#syncViewportPosition();
    });
  };

  #syncViewportPosition() {
    const header = this.closest('header-component') ?? document.getElementById('header-component');
    if (!(header instanceof HTMLElement)) return;

    const headerRect = header.getBoundingClientRect();
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const drawerTop = Math.max(0, Math.min(viewportHeight, headerRect.bottom));
    const availableHeight = Math.max(0, viewportHeight - drawerTop);

    this.style.setProperty('--menu-drawer-viewport-top', `${drawerTop}px`);
    this.style.setProperty('--menu-drawer-viewport-height', `${availableHeight}px`);
  }

  #startViewportTracking() {
    window.addEventListener('resize', this.#scheduleViewportSync, { passive: true });
    window.addEventListener('scroll', this.#scheduleViewportSync, { passive: true });
    window.visualViewport?.addEventListener('resize', this.#scheduleViewportSync, { passive: true });
    window.visualViewport?.addEventListener('scroll', this.#scheduleViewportSync, { passive: true });
  }

  #stopViewportTracking() {
    window.removeEventListener('resize', this.#scheduleViewportSync);
    window.removeEventListener('scroll', this.#scheduleViewportSync);
    window.visualViewport?.removeEventListener('resize', this.#scheduleViewportSync);
    window.visualViewport?.removeEventListener('scroll', this.#scheduleViewportSync);

    if (this.#viewportSyncFrame) {
      cancelAnimationFrame(this.#viewportSyncFrame);
      this.#viewportSyncFrame = 0;
    }
  }

  /**
   * Cancel any pending root drawer transition callback.
   */
  #cancelRootTransitionWait() {
    this.#rootTransitionToken += 1;

    if (this.#rootTransitionTimer) {
      window.clearTimeout(this.#rootTransitionTimer);
      this.#rootTransitionTimer = 0;
    }

    this.#rootTransitionCleanup?.();
    this.#rootTransitionCleanup = null;
  }

  /**
   * Run a callback after the root drawer's transform transition finishes.
   * A timeout fallback prevents the drawer from getting stuck if a browser
   * suppresses transitionend (for example after a visibility/viewport change).
   *
   * @param {HTMLElement} drawer
   * @param {() => void} callback
   */
  #afterRootDrawerTransition(drawer, callback) {
    this.#cancelRootTransitionWait();
    const token = this.#rootTransitionToken;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion) {
      requestAnimationFrame(() => {
        if (token === this.#rootTransitionToken) callback();
      });
      return;
    }

    let finished = false;

    const cleanup = () => {
      drawer.removeEventListener('transitionend', onTransitionEnd);
      if (this.#rootTransitionCleanup === cleanup) this.#rootTransitionCleanup = null;
    };

    const finish = () => {
      if (finished || token !== this.#rootTransitionToken) return;
      finished = true;
      cleanup();

      if (this.#rootTransitionTimer) {
        window.clearTimeout(this.#rootTransitionTimer);
        this.#rootTransitionTimer = 0;
      }

      callback();
    };

    const onTransitionEnd = (event) => {
      if (event.target !== drawer || event.propertyName !== 'transform') return;
      finish();
    };

    this.#rootTransitionCleanup = cleanup;
    drawer.addEventListener('transitionend', onTransitionEnd);
    this.#rootTransitionTimer = window.setTimeout(finish, 450);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Reset every nested mobile menu/mega-menu state without closing the root drawer.
   * This guarantees that a fresh drawer open always starts from the top-level menu.
   */
  #resetNestedDrawerState() {
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    this.refs.menuDrawer.scrollTop = 0;

    const referenceAccordions = this.refs.menuDrawer.querySelectorAll('details[data-mobile-drawer-accordion]');
    referenceAccordions.forEach((details) => {
      if (!(details instanceof HTMLDetailsElement)) return;
      this.#resetReferenceAccordion(details);
    });

    const nestedDetails = this.refs.menuDrawer.querySelectorAll('details[open]');
    nestedDetails.forEach((details) => {
      details.classList.remove('menu-open');
      details.removeAttribute('open');
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    });

    const expandedControls = this.refs.menuDrawer.querySelectorAll('[aria-expanded="true"]');
    expandedControls.forEach((control) => control.setAttribute('aria-expanded', 'false'));
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    const isRootDrawer = details === this.refs.details;

    if (isRootDrawer) {
      // Cancel a close callback immediately so a rapid reopen simply reverses
      // the current compositor transition instead of tearing the drawer down.
      this.#cancelRootTransitionWait();
      this.#resetNestedDrawerState();
      this.#syncViewportPosition();
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      // Keep the measured top fixed for the first animated frame. Starting
      // viewport tracking after the slide completes prevents sticky-header
      // movement from producing a visible jump during the transition.
      if (isRootDrawer) this.#syncViewportPosition();

      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

      if (isRootDrawer && drawer instanceof HTMLElement) {
        this.#afterRootDrawerTransition(drawer, () => {
          if (!details.classList.contains('menu-open')) return;
          this.#startViewportTracking();
          trapFocus(details);
        });
      } else {
        onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
      }
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    const isRootDrawer = details === this.refs.details;

    if (isRootDrawer) {
      // Freeze any in-progress mega accordion at its current rendered height so
      // the root drawer can slide out without its content changing underneath.
      this.#pauseAllReferenceAccordionAnimations();
      this.#stopViewportTracking();
    }

    // Keep the current drawer contents/state intact until the panel finishes
    // sliding out. Resetting nested mega menus before this point can change the
    // drawer height mid-transition and causes a visible jump/flicker.
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    const finishClose = () => {
      // A fast reopen reverses the current CSS transition. Never let the old
      // close callback remove the root <details> state after that reversal.
      if (isRootDrawer && details.classList.contains('menu-open')) return;

      reset(details);

      if (isRootDrawer) {
        this.#resetNestedDrawerState();
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    };

    if (isRootDrawer && drawer instanceof HTMLElement) {
      this.#afterRootDrawerTransition(drawer, finishClose);
    } else {
      onAnimationEnd(drawer || details, finishClose, { subtree: false });
    }
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Adds controlled accordion behavior to the reference mobile menu. Native
   * <details> closes immediately, so we intercept the summary click below
   * 1170px and animate the panel height before changing the final open state.
   */
  #setupReferenceAccordionListeners() {
    const summaries = this.querySelectorAll('details[data-mobile-drawer-accordion] > summary');
    summaries.forEach((summary) => summary.addEventListener('click', this.#onReferenceAccordionSummaryClick));
  }

  #removeReferenceAccordionListeners() {
    const summaries = this.querySelectorAll('details[data-mobile-drawer-accordion] > summary');
    summaries.forEach((summary) => summary.removeEventListener('click', this.#onReferenceAccordionSummaryClick));
  }

  #cancelReferenceAccordionAnimation(details) {
    const animation = this.#referenceAccordionAnimations.get(details);
    animation?.cancel();
    this.#referenceAccordionAnimations.delete(details);
    details.classList.remove('is-accordion-animating');
  }

  #cancelAllReferenceAccordionAnimations() {
    const accordions = this.querySelectorAll('details[data-mobile-drawer-accordion]');
    accordions.forEach((details) => {
      if (details instanceof HTMLDetailsElement) this.#cancelReferenceAccordionAnimation(details);
    });
  }

  #pauseAllReferenceAccordionAnimations() {
    const accordions = this.querySelectorAll('details[data-mobile-drawer-accordion]');
    accordions.forEach((details) => {
      if (!(details instanceof HTMLDetailsElement)) return;
      this.#referenceAccordionAnimations.get(details)?.pause();
    });
  }

  #resetReferenceAccordion(details) {
    this.#cancelReferenceAccordionAnimation(details);
    details.removeAttribute('data-accordion-target');
    details.removeAttribute('open');
    details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');

    const panel = details.querySelector(':scope > .menu-drawer__reference-panel');
    if (panel instanceof HTMLElement) {
      panel.style.removeProperty('height');
      panel.style.removeProperty('overflow');
      panel.style.removeProperty('opacity');
      panel.style.removeProperty('transform');
    }
  }

  /**
   * @param {Event} event
   */
  #onReferenceAccordionSummaryClick = (event) => {
    if (!window.matchMedia('(max-width: 1169px)').matches) return;

    const summary = event.currentTarget;
    if (!(summary instanceof HTMLElement)) return;

    const details = summary.parentElement;
    if (!(details instanceof HTMLDetailsElement) || !details.hasAttribute('data-mobile-drawer-accordion')) return;

    event.preventDefault();

    const targetState = details.getAttribute('data-accordion-target');
    const isVisuallyOpen = targetState === 'open' || (targetState === null && details.open);
    const shouldOpen = !isVisuallyOpen;

    if (shouldOpen) {
      const accordions = this.querySelectorAll('details[data-mobile-drawer-accordion]');
      accordions.forEach((otherDetails) => {
        if (!(otherDetails instanceof HTMLDetailsElement) || otherDetails === details) return;

        const otherTarget = otherDetails.getAttribute('data-accordion-target');
        if (otherDetails.open || otherTarget === 'open') {
          this.#setReferenceAccordionState(otherDetails, false);
        }
      });
    }

    this.#setReferenceAccordionState(details, shouldOpen);
  };

  /**
   * Smoothly open or close one top-level mobile accordion. The animation uses
   * the Web Animations API so rapid taps can reverse from the panel's current
   * rendered height without a flash or layout jump.
   *
   * @param {HTMLDetailsElement} details
   * @param {boolean} shouldOpen
   */
  #setReferenceAccordionState(details, shouldOpen) {
    const summary = details.querySelector(':scope > summary');
    const panel = details.querySelector(':scope > .menu-drawer__reference-panel');

    if (!(panel instanceof HTMLElement)) {
      details.open = shouldOpen;
      summary?.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
      return;
    }

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const wasRenderedOpen = details.open;
    const currentHeight = wasRenderedOpen ? panel.getBoundingClientRect().height : 0;
    const computedOpacity = Number.parseFloat(window.getComputedStyle(panel).opacity);
    const currentOpacity = wasRenderedOpen && Number.isFinite(computedOpacity) ? computedOpacity : 0;

    this.#cancelReferenceAccordionAnimation(details);
    details.setAttribute('data-accordion-target', shouldOpen ? 'open' : 'closed');
    summary?.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');

    if (shouldOpen && !details.open) {
      details.open = true;
    }

    if (reducedMotion) {
      details.open = shouldOpen;
      details.removeAttribute('data-accordion-target');
      panel.style.removeProperty('height');
      panel.style.removeProperty('overflow');
      panel.style.removeProperty('opacity');
      panel.style.removeProperty('transform');
      return;
    }

    const startHeight = shouldOpen ? currentHeight : Math.max(currentHeight, panel.getBoundingClientRect().height);
    const endHeight = shouldOpen ? panel.scrollHeight : 0;

    if (Math.abs(endHeight - startHeight) < 1) {
      details.open = shouldOpen;
      details.removeAttribute('data-accordion-target');
      panel.style.removeProperty('height');
      panel.style.removeProperty('overflow');
      panel.style.removeProperty('opacity');
      panel.style.removeProperty('transform');
      return;
    }

    panel.style.height = `${startHeight}px`;
    panel.style.overflow = 'hidden';
    details.classList.add('is-accordion-animating');

    const duration = Math.min(380, Math.max(260, 220 + Math.abs(endHeight - startHeight) * 0.08));
    const animation = panel.animate(
      [
        {
          height: `${startHeight}px`,
          opacity: Math.min(1, Math.max(0, currentOpacity)),
          transform: 'translate3d(0, 0, 0)',
        },
        {
          height: `${endHeight}px`,
          opacity: shouldOpen ? 1 : 0,
          transform: shouldOpen ? 'translate3d(0, 0, 0)' : 'translate3d(0, -6px, 0)',
        },
      ],
      {
        duration,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'forwards',
      }
    );

    this.#referenceAccordionAnimations.set(details, animation);

    const finish = () => {
      if (this.#referenceAccordionAnimations.get(details) !== animation) return;

      this.#referenceAccordionAnimations.delete(details);
      details.classList.remove('is-accordion-animating');
      details.open = shouldOpen;
      details.removeAttribute('data-accordion-target');
      summary?.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
      panel.style.removeProperty('height');
      panel.style.removeProperty('overflow');
      panel.style.removeProperty('opacity');
      panel.style.removeProperty('transform');
      animation.cancel();
    };

    animation.addEventListener('finish', finish, { once: true });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
